import type { BlockRegistry } from "../registry/registry.js";
import type { DataSource } from "../types/data.js";
import type { Theme } from "../types/theme.js";
import type { ReportTemplate } from "../types/template.js";
import { createId } from "../schema/ids.js";
import { checkProposal, type CheckProposalOptions } from "./accept.js";
import { describeDesigner } from "./describe.js";
import { redactAssets, redactDataModel } from "./redact.js";
import { INSTRUCTION_LIMIT, MAX_REPAIRS, TemplateAiError, type TemplateAiContext, type TemplateAiMode, type TemplateAiRequest, type TemplateAiResult } from "./types.js";

export interface CreateTemplateAiRequestOptions {
  mode: TemplateAiMode;
  instruction: string;
  registry: BlockRegistry;
  dataSources: DataSource[];
  theme?: Partial<Theme>;
  /** The design as it stands. For "create", the empty template the new design replaces. */
  current?: ReportTemplate;
  selection?: string[];
  locale?: string;
  /** Keep host-declared `enum`s in the data model. Off by default: see `redactDataModel`. */
  keepEnums?: boolean;
  /** Reuse a context already described for this registry and theme. */
  context?: TemplateAiContext;
  requestId?: string;
}

/**
 * A request ready to hand to a provider, and the images taken out of it.
 *
 * Everything that must not leave is removed here, in one place: examples from
 * the data model, embedded images from the template. Keep `assets` — it is what
 * `checkProposal` needs to put the images back.
 */
export function createTemplateAiRequest(opts: CreateTemplateAiRequestOptions): { request: TemplateAiRequest; assets: Map<string, string> } {
  const instruction = opts.instruction.trim();
  if (!instruction) throw new TemplateAiError("failed", "Say what you would like the report to be.");
  if (instruction.length > INSTRUCTION_LIMIT) throw new TemplateAiError("failed", `Keep the request to ${INSTRUCTION_LIMIT.toLocaleString("en-GB")} characters.`);
  const redacted = opts.current ? redactAssets(opts.current) : undefined;
  const request: TemplateAiRequest = {
    requestId: opts.requestId ?? createId("air"),
    mode: opts.mode,
    instruction,
    dataModel: redactDataModel(opts.dataSources, { keepEnums: opts.keepEnums }),
    context: opts.context ?? describeDesigner(opts.registry, opts.theme)
  };
  if (redacted) request.currentTemplate = redacted.template;
  if (opts.mode === "update" && opts.selection?.length) request.selection = [...opts.selection];
  if (opts.locale) request.locale = opts.locale;
  return { request, assets: redacted?.assets ?? new Map() };
}

export interface RunTemplateAiOptions extends CheckProposalOptions {
  generate(request: TemplateAiRequest, signal: AbortSignal): Promise<unknown>;
  signal: AbortSignal;
  /** At most `MAX_REPAIRS` (2); fewer if you like. */
  maxRepairs?: number;
  /** Called before each attempt, 0 for the first. */
  onAttempt?(attempt: number): void;
}

/**
 * Ask, check, and — when the answer fails — ask again with what was wrong.
 *
 * Never more than twice more: a model that cannot fix a template in two tries
 * with the exact problems in front of it is not going to on the third, and every
 * try is somebody's money. It calls nothing itself; `generate` is the host's.
 */
export async function runTemplateAi(opts: RunTemplateAiOptions): Promise<TemplateAiResult> {
  const repairs = Math.max(0, Math.min(MAX_REPAIRS, opts.maxRepairs ?? MAX_REPAIRS));
  let request: TemplateAiRequest = { ...opts.request };
  delete request.repair;
  for (let attempt = 0; ; attempt++) {
    if (opts.signal.aborted) throw cancelled();
    opts.onAttempt?.(attempt);
    let raw: unknown;
    try {
      raw = await opts.generate(request, opts.signal);
    } catch (e) {
      if (e instanceof TemplateAiError) throw e;
      if (opts.signal.aborted || (e as { name?: string })?.name === "AbortError") throw cancelled();
      throw new TemplateAiError("failed", "Something went wrong while designing that. Please try again.");
    }
    if (opts.signal.aborted) throw cancelled();
    const checked = checkProposal(raw, { ...opts, request });
    if (checked.ok) return checked.result;
    if (attempt >= repairs) {
      throw new TemplateAiError("invalid", "I couldn't make a design that passes BroadPaper's checks.", { hint: "Try asking for something smaller, or putting it another way." });
    }
    request = { ...opts.request, repair: { attempt: (attempt + 1) as 1 | 2, previousOutput: raw, problems: checked.problems } };
  }
}

function cancelled(): TemplateAiError {
  return new TemplateAiError("cancelled", "Cancelled.");
}
